import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { noop, omit } from "lodash";
import Drawer from "../../../../../components/Drawer";
import Button from "../../../../../components/Button";
import Table from "../../../../../components/NTable";
import Form from "./Form";
import { useNewTable } from "../../../../../components/NTable/hook";
import { useColumns } from "./useColumns";
import { useTimeOffVersionsApi } from "./api";
import { useDeleteModal } from "../../../helpers/useDeleteModal";

const TimeOffVersion = ({ open, close, id }) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState({ mode: "view" });
    const goToEdit = useCallback((values) => setSelected({ mode: "edit", values }), [setSelected]);

    const {
        getVersions,
        createVersion,
        updateVersion,
        removeVersion,
        data,
        totalItems,
        loading,
    } = useTimeOffVersionsApi();

    const { open: openDeleteModal, modal: deleteModal } = useDeleteModal({
        message: t("do-you-want-to-delete-version"),
        onConfirm: (versionId) => removeVersion(versionId, id),
        permission: "settings.payPolicies.timeOff.canDelete",
    });

    const columns = useColumns({
        edit: goToEdit,
        remove: openDeleteModal,
    });

    const table = useNewTable({
        data,
        totalItems,
        loading,
        columns,
        enableRowSelection: false,
    });

    const title = useMemo(() => {
        switch(selected?.mode) {
            case "create":
                return t("create-new-version");
            case "edit":
                return t("edit-version")
            case "view":
            default: 
                return t("time-off-versions")
        }
    }, [selected?.mode, t]);

    const onClick = useCallback(() => {
        setSelected((prev) => (prev.mode === "view" ? { mode: "create" } : { mode: "view" }));
    }, []);

    const onSuccess = useCallback(() => setSelected({ mode: "view" }), []);

    const formProps = useMemo(() => {
        const mode = selected?.mode;
        const values = {
            ...selected?.values,
            ...omit(selected?.values?.data, ["id"]),
        };
        return {
            mode,
            submit: (_data) => {
                const data = omit(_data, ["status"]);
                if (selected?.mode === "create") {
                    return createVersion(id, data, onSuccess);
                } else {
                    return updateVersion(id, values?.id, data, onSuccess);
                }
            },
            entityId: id,
            close: () => setSelected({ mode: "view" }),
            values: omit(values, ["createdBy", "data", "entityId"]),
        };
    }, [selected, createVersion, id, onSuccess, updateVersion]);

    useEffect(() => {
        const controller = new AbortController();
        getVersions(id, controller);
        return () => controller.abort();
    }, [getVersions, id]);

    return (
        <>
            <Drawer title={title} open={open} close={close}>
                <div className="mb-3">
                    <Button color="primary" onClick={onClick}>
                        {selected.mode === "view" ? t("create") : t("view-table")}
                    </Button>
                </div>
                {selected.mode === "view" ? (
                    <div style={{ height: "calc(100% - 50px)" }}>
                        <Table table={table} />
                    </div>
                ) : (
                    <Form {...formProps} />
                )}
            </Drawer>

            {deleteModal}
        </>
    );
};

export default TimeOffVersion;
